import { useEffect, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material'
import { HCPFlowEnum, IHCPFlowStatus } from '../../../Services/HCP/flows/hcp-flow.interface'
import { CreateProductionBatchPayload, ProductionBatch } from '../../../Services/HCP/flows/production-batch.interface'
import { productionBatchService } from '../../../Services/HCP/flows/production-batch.service'
import { batchQuantityLimit, isBatchDestinationAllowed, validateBatchCreation } from '../../../Services/HCP/flows/production-batch.helpers'
import { getHCPFlowErrorMessage } from '../../../Services/HCP/flows/hcp-flow-error.helpers'
import { useAuth } from '../../../Hooks/Auth'
import { canCreateHCPDerivedBatches } from '../../../Services/HCP/flows/hcp-flow-permissions.helpers'

type Kind = 'split' | 'reworks'
type Pending = { kind: Kind; body: CreateProductionBatchPayload }
const flows = [
  { value: HCPFlowEnum.SHEET, label: 'Montagem de Cartelas' },
  { value: HCPFlowEnum.PRODUCTION, label: 'Produção' },
  { value: HCPFlowEnum.MACHINE_PRODUCTION, label: 'Produção de Máquinas' },
  { value: HCPFlowEnum.LOGISTICS, label: 'Logística' },
]

// Keep the exact confirmed operation across a dialog close or page reload.
function readPending(key: string): Pending | null {
  try { return JSON.parse(sessionStorage.getItem(key) ?? 'null') } catch { return null }
}

export default function ProductionBatchDialog({ batchId, title, kind, onClose, onChanged }: {
  batchId: string; title: string; kind: Kind; onClose: () => void; onChanged: () => void
}) {
  const { user } = useAuth()
  const canCreate = canCreateHCPDerivedBatches(user)
  const storageKey = `hcp:batch-operation:${batchId}`
  const [pending, setPending] = useState<Pending | null>(() => readPending(storageKey))
  const operationKind = pending?.kind ?? kind
  const [batch, setBatch] = useState<ProductionBatch | null>(null)
  const [flow, setFlow] = useState(pending?.body.flow ?? HCPFlowEnum.PRODUCTION)
  const [statuses, setStatuses] = useState<IHCPFlowStatus[]>([])
  const [statusId, setStatusId] = useState(pending?.body.hcpFlowStatusId ?? '')
  const [quantity, setQuantity] = useState(pending ? String(pending.body.quantity) : '')
  const [reason, setReason] = useState(pending?.body.reason ?? '')
  const [loading, setLoading] = useState(true)
  const [loadingStatuses, setLoadingStatuses] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    productionBatchService.get(batchId).then((value) => { if (active) setBatch(value) })
      .catch((failure) => { if (active) setError(getHCPFlowErrorMessage(failure, 'Erro ao consultar lote.')) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [batchId])

  useEffect(() => {
    let active = true
    setStatuses([])
    setLoadingStatuses(true)
    productionBatchService.statuses(flow).then((value) => {
      if (active) setStatuses(value.filter((status) => isBatchDestinationAllowed(operationKind, flow, status.sequence)))
    }).catch((failure) => { if (active) setError(getHCPFlowErrorMessage(failure, 'Erro ao consultar etapas.')) })
      .finally(() => { if (active) setLoadingStatuses(false) })
    return () => { active = false }
  }, [flow, operationKind])

  const status = statuses.find((item) => item.id === statusId)
  const clearPending = () => { sessionStorage.removeItem(storageKey); setPending(null) }
  const submit = async () => {
    if (!canCreate) return
    if (!batch || submittingRef.current) return
    const validation = validateBatchCreation(batch, operationKind, Number(quantity), status, reason)
    if (!pending && validation) { setError(validation); return }
    if (!pending && !confirming) { setError(''); setConfirming(true); return }
    const operation: Pending = pending ?? { kind: operationKind, body: {
      quantity: Number(quantity), flow, hcpFlowStatusId: statusId,
      ...(operationKind === 'reworks' ? { reason: reason.trim() } : {}),
      operationId: uuid(), expectedVersion: batch.version,
    } }
    // Persist before sending: a failed connection may still have committed on the server.
    try { sessionStorage.setItem(storageKey, JSON.stringify(operation)) } catch {
      setError('Não foi possível guardar a operação para reenvio. Habilite o armazenamento da sessão e tente novamente.')
      return
    }
    setPending(operation)
    submittingRef.current = true
    setSubmitting(true)
    setError('')
    try {
      await productionBatchService.create(batchId, operation.kind, operation.body)
      clearPending()
      onChanged()
    } catch (failure: any) {
      const message = getHCPFlowErrorMessage(failure, 'Não foi possível confirmar a operação.')
      if ([400, 401, 403, 404, 422].includes(failure?.response?.status)) {
        clearPending()
        setConfirming(false)
        setLoading(true)
        setBatch(null)
        try { setBatch(await productionBatchService.get(batchId)) } catch { /* Keep submission blocked until a new read succeeds. */ }
        finally { setLoading(false) }
        setError(`${message} Confira os dados atualizados e confirme novamente.`)
      } else {
        setError(`${message} O resultado está incerto. Reenvie a mesma operação para confirmar; os dados ficam preservados.`)
      }
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onClose={() => { if (!submitting) onClose() }} fullWidth maxWidth="sm">
      <DialogTitle>{operationKind === 'split' ? 'Dividir lote' : 'Criar retrabalho'}</DialogTitle>
      <DialogContent>
        <Typography fontWeight={600} sx={{ mb: 2 }}>{title}</Typography>
        {loading && <CircularProgress size={24} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {pending && <Alert severity="warning" sx={{ mb: 2 }}>Existe uma operação pendente de confirmação. O reenvio usa a mesma quantidade, destino e chave, evitando duplicação.</Alert>}
        {batch && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2">Quantidade inicial: {batch.initialQuantity.toLocaleString('pt-BR')} · Saldo: {batch.quantity.toLocaleString('pt-BR')}</Typography>
          <TextField label="Quantidade" type="number" value={quantity} disabled={Boolean(pending) || submitting || confirming}
            inputProps={{ min: 1, max: batchQuantityLimit(batch, operationKind), step: 1 }}
            onChange={(event) => setQuantity(event.target.value)} helperText={`Máximo: ${batchQuantityLimit(batch, operationKind)}`} />
          <TextField select label="Fluxo de destino" value={flow} disabled={Boolean(pending) || submitting || confirming}
            onChange={(event) => { setFlow(event.target.value as HCPFlowEnum); setStatusId('') }}>
            {flows.filter((item) => operationKind === 'split' || item.value !== HCPFlowEnum.LOGISTICS)
              .map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>
          <TextField select label="Etapa de destino" value={loadingStatuses ? '' : statusId} disabled={Boolean(pending) || submitting || confirming || loadingStatuses}
            onChange={(event) => setStatusId(event.target.value)}>
            {statuses.map((item) => <MenuItem key={item.id} value={item.id}>{item.displayCode} — {item.name}</MenuItem>)}
          </TextField>
          {operationKind === 'reworks' && <TextField label="Motivo do retrabalho" required multiline minRows={2} value={reason}
            disabled={Boolean(pending) || submitting || confirming} onChange={(event) => setReason(event.target.value)} />}
          {confirming && !pending && <Alert severity="info">
            {operationKind === 'split'
              ? `Separar ${quantity} unidades. O lote original ficará com ${batch.quantity - Number(quantity)} unidades.`
              : `Produzir ${quantity} unidades de retrabalho. O saldo do original permanece ${batch.quantity}.`}
            {` O novo lote seguirá para ${status?.displayCode} — ${status?.name} e nascerá sem Pré-OS.`}
          </Alert>}
        </Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Fechar</Button>
        {confirming && !pending && <Button disabled={submitting} onClick={() => setConfirming(false)}>Revisar</Button>}
        <Button variant="contained" onClick={submit} disabled={!canCreate || !batch || loading || submitting || (!pending && loadingStatuses)}>
          {submitting ? <CircularProgress size={20} /> : pending ? 'Reenviar mesma operação' : confirming ? 'Confirmar' : 'Continuar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
